import {IRange} from "@pg-design/inputs-module";
import {decimalSeparatorThousand} from "@pg-mono/string-utils";
import {pluralize} from "@pg-mono/string-utils";
import {Currency} from "@pg-rp/common-types";

import {ISearchInputValue} from "../components/ISearchInputValue";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {SearchTab} from "../types/SearchTab";
import {stringifyConstructionEndDate} from "./stringify_construction_end_date";

interface IFormatRecentFilterTextParams {
    filters: Partial<ISearchFiltersFormValues>;
    search?: ISearchInputValue;
    currency?: Currency;
}

const hasRangeShape = (value: unknown): value is IRange<unknown> =>
    !!value && typeof value === "object" && ("lower" in (value as object) || "upper" in (value as object));

/** "1 pokój", "3 pokoje" or "2-4 pokoje" */
const formatRoomsPart = (rooms: unknown): string | null => {
    if (!hasRangeShape(rooms)) return null;

    const {lower, upper} = rooms as IRange<number | "">;

    const roomsPlural = pluralize(["pokój", "pokoje", "pokoi"]);

    if (lower && upper) {
        return lower === upper ? `${lower} ${roomsPlural(lower as number)}` : `${lower}-${upper} ${roomsPlural(upper as number)}`;
    }
    if (upper) return `${upper} ${roomsPlural(upper as number)}`;
    if (lower) return `od ${lower} ${roomsPlural(lower as number)}`;

    return null;
};

/** "22-56 m2", "do 80 m2", "od 30 m2" */
const formatAreaPart = (area: unknown): string | null => {
    if (!hasRangeShape(area)) return null;

    const {lower, upper} = area as IRange<string>;

    if (lower && upper) return `${decimalSeparatorThousand(lower)}-${decimalSeparatorThousand(upper)} m2`;
    if (upper) return `do ${decimalSeparatorThousand(upper)} m2`;
    if (lower) return `od ${decimalSeparatorThousand(lower)} m2`;
    return null;
};

/** "22–56 m²", "do 80 m²", "od 30 m²" — hero / Figma formatting. */
const formatAreaPartForHero = (area: unknown): string | null => {
    const formattedArea = formatAreaPart(area);

    if (!formattedArea) {
        return null;
    }

    return formattedArea.replace(/([\d\s\u00A0]+)-([\d\s\u00A0]+) m2/g, "$1–$2 m²").replace(/ m2/g, " m²");
};

/** "Gotowe do odbioru", "Do 3 miesięcy", "Do 6 miesięcy", "Do roku", "Do 3 lat" (single or multiple joined). */
const formatConstructionEndDatePart = (constructionEndDate: unknown): string | null => {
    if (!Array.isArray(constructionEndDate) || constructionEndDate.length === 0) return null;
    const str = stringifyConstructionEndDate(constructionEndDate);
    return str || null;
};

/** Instalment ("rata do X zł") or price ("do X zł"); instalment takes precedence. */
const formatInstalmentOrPricePart = (filters: Partial<ISearchFiltersFormValues>, currency: Currency): string | null => {
    const instalment = filters.instalment;
    const hasInstalment = instalment !== undefined && instalment !== "" && typeof instalment === "number" && instalment > 0;

    if (hasInstalment) {
        const unit = currency === Currency.EURO ? "eur" : "zł";
        return `rata do ${decimalSeparatorThousand(instalment.toString())} ${unit}`;
    }

    if (hasRangeShape(filters.price)) {
        return formatPriceFilter(filters.price as IRange<string>, currency) || null;
    }

    return null;
};

/**
 * Formats recent filter values into a readable text string.
 * Location first, then filters, then construction end date (termin oddania).
 * Examples:
 * - "Lublin, 3 pokoje, do 450 000 zł"
 * - "Warszawa: Mokotów, Wilanów, Wawer, Targówek, 3 pokoje, 22-56 m2, do 890 000 zł, do 3 miesięcy"
 */
export const formatRecentFilterText = (params: IFormatRecentFilterTextParams): string => {
    const {location, filterParts} = getRecentFilterDisplayParts(params);

    return [location, ...filterParts].filter((part) => part !== "").join(", ");
};

export interface IRecentFilterDisplayParts {
    location: string;
    rooms: string | null;
    area: string | null;
    price: string | null;
    constructionEndDate: string | null;
    /** Non-empty filter labels in display order. */
    filterParts: string[];
}

/**
 * Splits a recent filter into a location line and individual filter labels.
 * Homepage hero uses the location as the title and joins filterParts with " · ".
 * The labels are kept separate because analytics reports them one by one.
 */
export const getRecentFilterDisplayParts = (params: IFormatRecentFilterTextParams): IRecentFilterDisplayParts => {
    const {filters, search, currency = Currency.PLN} = params;
    const rooms = filters ? formatRoomsPart(filters.rooms) : null;
    const area = filters ? formatAreaPartForHero(filters.area) : null;
    const price = filters ? formatInstalmentOrPricePart(filters, currency) : null;
    const constructionEndDate = filters ? formatConstructionEndDatePart(filters.construction_end_date) : null;

    return {
        location: formatLocationPart(search) || "",
        rooms,
        area,
        price,
        constructionEndDate,
        filterParts: [rooms, area, price, constructionEndDate].filter((part): part is string => part != null && part !== "")
    };
};

/**
 * Formats location from search regions.
 * Single region: "Lublin"
 * Multiple regions with same parent (e.g. city districts): "Warszawa: Mokotów, Wilanów, Wawer, Targówek"
 * Multiple regions with different parents: "Lublin, Kraków"
 */
const formatLocationPart = (search?: ISearchInputValue): string | null => {
    if (!search || search.tabType !== SearchTab.Regions || !search.regions?.length) {
        return null;
    }
    const regions = search.regions;

    if (regions.length === 1) {
        return regions[0].full_name_reverted || regions[0].name || null;
    }

    const firstParent = regions[0].parent;
    const sameParent = firstParent && regions.every((r) => r.parent?.id === firstParent.id);

    if (sameParent) {
        const districtNames = regions.map((r) => r.name).join(", ");
        return `${firstParent.name}: ${districtNames}`;
    }

    return regions.map((r) => r.full_name_reverted || r.name).join(", ");
};

/**
 * Formats price filter into text
 */
const formatPriceFilter = (price: IRange<string>, currency: Currency): string => {
    const {lower, upper} = price;
    const currencyUnit = currency === Currency.EURO ? "eur" : "zł";

    if (!lower && !upper) {
        return "";
    }

    if (lower && upper) {
        return `do ${decimalSeparatorThousand(upper)} ${currencyUnit}`;
    }

    if (upper) {
        return `do ${decimalSeparatorThousand(upper)} ${currencyUnit}`;
    }

    if (lower) {
        return `od ${decimalSeparatorThousand(lower)} ${currencyUnit}`;
    }

    return "";
};
import {IRange} from "@pg-design/inputs-module";
import {decimalSeparatorThousand} from "@pg-mono/string-utils";
import {pluralize} from "@pg-mono/string-utils";
import {Currency} from "@pg-rp/common-types";

import {ISearchInputValue} from "../components/ISearchInputValue";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {SearchTab} from "../types/SearchTab";
import {stringifyConstructionEndDate} from "./stringify_construction_end_date";

interface IFormatRecentFilterTextParams {
    filters: Partial<ISearchFiltersFormValues>;
    search?: ISearchInputValue;
    currency?: Currency;
}

const hasRangeShape = (value: unknown): value is IRange<unknown> =>
    !!value && typeof value === "object" && ("lower" in (value as object) || "upper" in (value as object));

/** "1 pokój", "3 pokoje" or "2-4 pokoje" */
const formatRoomsPart = (rooms: unknown): string | null => {
    if (!hasRangeShape(rooms)) return null;

    const {lower, upper} = rooms as IRange<number | "">;

    const roomsPlural = pluralize(["pokój", "pokoje", "pokoi"]);

    if (lower && upper) {
        return lower === upper ? `${lower} ${roomsPlural(lower as number)}` : `${lower}-${upper} ${roomsPlural(upper as number)}`;
    }
    if (upper) return `${upper} ${roomsPlural(upper as number)}`;
    if (lower) return `od ${lower} ${roomsPlural(lower as number)}`;

    return null;
};

/** "22-56 m2", "do 80 m2", "od 30 m2" */
const formatAreaPart = (area: unknown): string | null => {
    if (!hasRangeShape(area)) return null;

    const {lower, upper} = area as IRange<string>;

    if (lower && upper) return `${decimalSeparatorThousand(lower)}-${decimalSeparatorThousand(upper)} m2`;
    if (upper) return `do ${decimalSeparatorThousand(upper)} m2`;
    if (lower) return `od ${decimalSeparatorThousand(lower)} m2`;
    return null;
};

/** "22–56 m²", "do 80 m²", "od 30 m²" — hero / Figma formatting. */
const formatAreaPartForHero = (area: unknown): string | null => {
    const formattedArea = formatAreaPart(area);

    if (!formattedArea) {
        return null;
    }

    return formattedArea.replace(/([\d\s\u00A0]+)-([\d\s\u00A0]+) m2/g, "$1–$2 m²").replace(/ m2/g, " m²");
};

/** "Gotowe do odbioru", "Do 3 miesięcy", "Do 6 miesięcy", "Do roku", "Do 3 lat" (single or multiple joined). */
const formatConstructionEndDatePart = (constructionEndDate: unknown): string | null => {
    if (!Array.isArray(constructionEndDate) || constructionEndDate.length === 0) return null;
    const str = stringifyConstructionEndDate(constructionEndDate);
    return str || null;
};

/** Instalment ("rata do X zł") or price ("do X zł"); instalment takes precedence. */
const formatInstalmentOrPricePart = (filters: Partial<ISearchFiltersFormValues>, currency: Currency): string | null => {
    const instalment = filters.instalment;
    const hasInstalment = instalment !== undefined && instalment !== "" && typeof instalment === "number" && instalment > 0;

    if (hasInstalment) {
        const unit = currency === Currency.EURO ? "eur" : "zł";
        return `rata do ${decimalSeparatorThousand(instalment.toString())} ${unit}`;
    }

    if (hasRangeShape(filters.price)) {
        return formatPriceFilter(filters.price as IRange<string>, currency) || null;
    }

    return null;
};

/**
 * Formats recent filter values into a readable text string.
 * Location first, then filters, then construction end date (termin oddania).
 * Examples:
 * - "Lublin, 3 pokoje, do 450 000 zł"
 * - "Warszawa: Mokotów, Wilanów, Wawer, Targówek, 3 pokoje, 22-56 m2, do 890 000 zł, do 3 miesięcy"
 */
export const formatRecentFilterText = (params: IFormatRecentFilterTextParams): string => {
    const {location, filterParts} = getRecentFilterDisplayParts(params);

    return [location, ...filterParts].filter((part) => part !== "").join(", ");
};

export interface IRecentFilterDisplayParts {
    location: string;
    rooms: string | null;
    area: string | null;
    price: string | null;
    constructionEndDate: string | null;
    /** Non-empty filter labels in display order. */
    filterParts: string[];
}

/**
 * Splits a recent filter into a location line and individual filter labels.
 * Homepage hero uses the location as the title and joins filterParts with " · ".
 * The labels are kept separate because analytics reports them one by one.
 */
export const getRecentFilterDisplayParts = (params: IFormatRecentFilterTextParams): IRecentFilterDisplayParts => {
    const {filters, search, currency = Currency.PLN} = params;
    const rooms = filters ? formatRoomsPart(filters.rooms) : null;
    const area = filters ? formatAreaPartForHero(filters.area) : null;
    const price = filters ? formatInstalmentOrPricePart(filters, currency) : null;
    const constructionEndDate = filters ? formatConstructionEndDatePart(filters.construction_end_date) : null;

    return {
        location: formatLocationPart(search) || "",
        rooms,
        area,
        price,
        constructionEndDate,
        filterParts: [rooms, area, price, constructionEndDate].filter((part): part is string => part != null && part !== "")
    };
};

/**
 * Formats location from search regions.
 * Single region: "Lublin"
 * Multiple regions with same parent (e.g. city districts): "Warszawa: Mokotów, Wilanów, Wawer, Targówek"
 * Multiple regions with different parents: "Lublin, Kraków"
 */
const formatLocationPart = (search?: ISearchInputValue): string | null => {
    if (!search || search.tabType !== SearchTab.Regions || !search.regions?.length) {
        return null;
    }
    const regions = search.regions;

    if (regions.length === 1) {
        return regions[0].full_name_reverted || regions[0].name || null;
    }

    const firstParent = regions[0].parent;
    const sameParent = firstParent && regions.every((r) => r.parent?.id === firstParent.id);

    if (sameParent) {
        const districtNames = regions.map((r) => r.name).join(", ");
        return `${firstParent.name}: ${districtNames}`;
    }

    return regions.map((r) => r.full_name_reverted || r.name).join(", ");
};

/**
 * Formats price filter into text
 */
const formatPriceFilter = (price: IRange<string>, currency: Currency): string => {
    const {lower, upper} = price;
    const currencyUnit = currency === Currency.EURO ? "eur" : "zł";

    if (!lower && !upper) {
        return "";
    }

    if (lower && upper) {
        return `do ${decimalSeparatorThousand(upper)} ${currencyUnit}`;
    }

    if (upper) {
        return `do ${decimalSeparatorThousand(upper)} ${currencyUnit}`;
    }

    if (lower) {
        return `od ${decimalSeparatorThousand(lower)} ${currencyUnit}`;
    }

    return "";
};
